/**
 * #1073 — the client half of Pinterest's OAuth round trip.
 *
 * Same-window redirect, like Kroger (useKroger.tsx), so sessionStorage survives
 * the trip out to pinterest.com and back. Two things ride along:
 *
 *   - `state`: random, made on her device, checked on the way back BEFORE the
 *     code is exchanged. A callback carrying someone else's code (a login CSRF
 *     that would attach THEIR boards to HER account) fails that check.
 *   - the return path, so she lands back where she tapped Connect.
 *
 * ⚠️ Both are read ONCE and cleared, whether or not they match — a value we
 * refuse must not survive to be accepted on a later callback.
 */
import { safeReturnPath } from "@/lib/safeReturnPath";

export const PINTEREST_STATE_KEY = "kin_pinterest_oauth_state";
export const PINTEREST_RETURN_PATH_KEY = "kin_pinterest_return_path";

/** 32 URL-safe characters from the platform CSPRNG. Matches the server's
 *  isValidOAuthState (16-128 of [A-Za-z0-9_-]). */
export function makeOAuthState(cryptoImpl: Pick<Crypto, "getRandomValues"> = crypto): string {
  const bytes = new Uint8Array(24);
  cryptoImpl.getRandomValues(bytes);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function rememberPinterestOAuth(state: string, returnPath: string, storage: Storage = sessionStorage): boolean {
  try {
    storage.setItem(PINTEREST_STATE_KEY, state);
    storage.setItem(PINTEREST_RETURN_PATH_KEY, returnPath);
    return true;
  } catch {
    // Private browsing or quota. Without a stored state the callback cannot
    // verify anything, so the caller must not start the redirect.
    return false;
  }
}

/** Reads and clears both keys. `stateOk` is true only for an exact match with
 *  a state we actually stored. */
export function consumePinterestOAuth(
  returnedState: string | null,
  origin: string,
  storage: Storage = sessionStorage,
): { stateOk: boolean; returnPath: string } {
  let stored: string | null = null;
  let path: string | null = null;
  try {
    stored = storage.getItem(PINTEREST_STATE_KEY);
    path = storage.getItem(PINTEREST_RETURN_PATH_KEY);
    storage.removeItem(PINTEREST_STATE_KEY);
    storage.removeItem(PINTEREST_RETURN_PATH_KEY);
  } catch {
    // unreadable storage: stored stays null, so the state check fails closed
  }
  const stateOk = !!stored && !!returnedState && stored === returnedState;
  return { stateOk, returnPath: (path && safeReturnPath(path, origin)) || "/" };
}
